export type MFARequirement =
  | { readonly status: 'not_required' }
  | { readonly status: 'required'; readonly factorId: string }
  | { readonly status: 'indeterminate' };

export interface MfaPolicyEvidence {
  readonly currentLevel: string | null;
  readonly nextLevel: string | null;
  readonly authenticationMethods?: unknown;
  readonly platformAdmin?: boolean | null;
  readonly factors?:
    | { readonly available: true; readonly verifiedFactorId: string | null }
    | { readonly available: false };
}

export function hasPasskeyAuthentication(methods: unknown): boolean {
  if (!Array.isArray(methods)) {
    return false;
  }
  return methods.some(
    (entry: unknown) =>
      entry === 'passkey' ||
      (entry !== null &&
        typeof entry === 'object' &&
        'method' in entry &&
        entry.method === 'passkey'),
  );
}

/** Evaluates verified session evidence; lookup_required never authorizes access. */
export function evaluateMfaRequirement(
  evidence: MfaPolicyEvidence,
): MFARequirement | { readonly status: 'lookup_required' } {
  const { currentLevel, nextLevel, factors } = evidence;
  if (
    !['aal1', 'aal2'].includes(currentLevel ?? '') ||
    !['aal1', 'aal2'].includes(nextLevel ?? '')
  ) {
    return { status: 'indeterminate' };
  }
  if (currentLevel !== 'aal1' || nextLevel !== 'aal2') {
    return { status: 'not_required' };
  }
  if (
    hasPasskeyAuthentication(evidence.authenticationMethods) &&
    evidence.platformAdmin === false
  ) {
    return { status: 'not_required' };
  }
  if (!factors) {
    return { status: 'lookup_required' };
  }
  if (!factors.available) {
    return { status: 'indeterminate' };
  }
  return factors.verifiedFactorId
    ? { status: 'required', factorId: factors.verifiedFactorId }
    : { status: 'not_required' };
}
